import { useEffect, useState } from "react";
import { TransactionsList } from "../utils/types";
import {
  Button,
  ButtonGroup,
  Center,
  Checkbox,
  CheckboxGroup,
  HStack,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { getHeadersForRequest } from "../utils/functions";
import axios from "axios";
import { Loading } from "../components/Loading";
import { FaTrash } from "react-icons/fa";
import Nav from "../components/navbar";
import { TransactionCard } from "../components/TransactionCard";
import { useNavigate } from "react-router-dom";

const PendingOrdersScreen = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<TransactionsList[]>([]);
  const navigate = useNavigate();
  const [groupValues, setGroupValues] = useState<string[]>([]);
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState<string>("");
  const [transactionId, setTransactionId] = useState<number>(0);
  const [traReceiptNumber, setTraReceiptNumber] = useState<string>("");
  const [traReceiptTime, setTraReceiptTime] = useState<string>("");
  const toast = useToast();

  const navigateToOrderDetails = (orderId: string) => {
    navigate(`/OrderDetails/${orderId}`);
  };

  useEffect(() => {
    setIsLoading(true);
    getAllTransactions();
  }, []);

  const getAllTransactions = async () => {
    try {
      const headers = await getHeadersForRequest();
      const response = await axios.get<TransactionsList[]>(
        `${process.env.REACT_APP_TEST_API_URL}/transactions/getall`,
        {
          headers,
        }
      );

      console.log(response);
      const filteredOrdersDeliveryPending = await response.data.filter(
        (transaction) =>
          transaction.deliveryStatus === false &&
          transaction.isVoid === false &&
          transaction.transactionType === 1
      );

      setTransactions(
        filteredOrdersDeliveryPending.sort(
          (a, b) =>
            new Date(b.invoiceDate).getTime() -
            new Date(a.invoiceDate).getTime()
        )
      );
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const groupedTransactions: {
    date: string;
    transactions: TransactionsList[];
  }[] = [];

  // Sort the groupedTransactions array in descending order of transaction date
  groupedTransactions.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });

  for (let transaction of transactions) {
    const date = new Date(transaction.invoiceDate).toLocaleDateString();
    const existingGroup = groupedTransactions.find(
      (group) => group.date === date
    );
    if (existingGroup) {
      existingGroup.transactions.push(transaction);
    } else {
      groupedTransactions.push({ date, transactions: [transaction] });
    }
  }

  const handleTransactionClick = (transaction: TransactionsList) => {
    setShowDetailsModal(true);

    let delivered = transaction.deliveryStatus ? "delivered" : "";
    let paid = transaction.paymentStatus ? "paid" : "";
    let physical = transaction.physicalDeliveryNote ? "physical" : "";
    let tra = transaction.traReceiptNumber && transaction.traTime ? "tra" : "";
    let newGroupValues = [delivered, paid, physical, tra];

    setGroupValues(newGroupValues);
    setDeliveryNoteNumber(transaction.deliveryNoteNumber);
    setTransactionId(transaction.id);
  };

  const updateTransactionDetails = async () => {
    try {
      const data = {
        paymentStatus: groupValues.includes("paid"),
        paymentDate: groupValues.includes("paid") ? new Date() : null,
        transactionId: transactionId,
        deliveryStatus: groupValues.includes("delivered"),
        deliveryDate: groupValues.includes("delivered") ? new Date() : null,
        invoiceDate: new Date(),
        physicalDeliveryNote: groupValues.includes("physical"),
        deliveryNoteNumber: groupValues.includes("physical")
          ? deliveryNoteNumber
          : "",
        traReceiptNumber: traReceiptNumber,
        traReceiptTime: traReceiptTime,
      };

      if (data.deliveryNoteNumber === "" && data.physicalDeliveryNote) {
        toast({
          title: "Invalid Delivery Note Number",
          status: "warning",
          duration: 9000,
          isClosable: true,
        });
        return;
      }

      const headers = await getHeadersForRequest();
      await axios
        .post(
          `${process.env.REACT_APP_TEST_API_URL}/transactions/update`,
          {
            data,
          },
          { headers }
        )
        .then((response) => {
          console.log(response.data);
          toast({
            title: "Transaction details updated successfully",
            status: "warning",
            duration: 9000,
            isClosable: true,
          });
          setShowDetailsModal(false);
          getAllTransactions();
        })
        .catch((error) => {
          console.error(error);
          toast({
            title: "Error updating transaction",
            status: "warning",
            duration: 9000,
            isClosable: true,
          });
        });
    } catch (error) {}
  };

  const voidTransaction = async () => {
    try {
      const headers = await getHeadersForRequest();
      await axios
        .post(
          `${process.env.REACT_APP_TEST_API_URL}/transactions/delete`,
          {
            transactionId: transactionId,
          },
          { headers }
        )
        .then((response) => {
          console.log(response.data);
          toast({
            title: "Transaction VOID successful",
            status: "warning",
            duration: 9000,
            isClosable: true,
          });
          setShowDetailsModal(false);
          getAllTransactions();
        })
        .catch((error) => {
          console.error(error);
          toast({
            title: "Error, please try again later",
            status: "warning",
            duration: 9000,
            isClosable: true,
          });
        });
    } catch (error) {
      // toast({ title: error.toString() });,status: "warning",duration: 9000,isClosable: true,
      console.error(error);
    }
  };

  return (
    <Stack>
      <Stack position="sticky" top={0} zIndex={1} width="full">
        <Nav pageName={"Pending Orders"}/>
      </Stack>
      <Stack flex={1}>
        <Stack pt={2} pb={10} px={3}>
          {isLoading ? (
            <Center h={600}>
              <Loading />
            </Center>
          ) : (
            <Stack>
              <Text>Record count: {groupedTransactions.length}</Text>
              {groupedTransactions.map((groupedTransaction, index) => (
                <Stack key={index}>
                  {/* <Spacer size={10} /> */}
                  <Heading
                    // color={primary}
                    size="md"
                    my={2}
                  >
                    {groupedTransaction.date}
                  </Heading>
                  {groupedTransaction.transactions.map((transaction, index) => (
                    <>
                      <Stack
                        key={index}
                        onDoubleClick={() => {
                          if (transaction.isVoid) {
                            toast({
                              title: "Cannot edit a VOID receipt",
                              status: "warning",
                              duration: 9000,
                              isClosable: true,
                            });
                          } else {
                            handleTransactionClick(transaction);
                          }
                        }}
                        onClick={() =>
                          navigateToOrderDetails(transaction.transactionNumber)
                        }
                      >
                        <TransactionCard transaction={transaction} />
                      </Stack>
                    </>
                  ))}
                </Stack>
              ))}
              {groupedTransactions.length < 1 && (
                <Stack alignItems={"center"} justifyContent={"center"} h={400}>
                  <Text fontSize={20} textAlign={"center"}>
                    There are no pending orders!
                  </Text>
                </Stack>
              )}
            </Stack>
          )}

          {/* TRANSACTION DETAILS MODAL */}
          <Modal
            closeOnOverlayClick={false}
            isOpen={showDetailsModal}
            onClose={() => setShowDetailsModal(false)}
          >
            <ModalContent maxWidth="350">
              <ModalCloseButton />
              <ModalHeader>Transaction Details</ModalHeader>
              <ModalBody>
                <HStack justifyContent={"space-between"}>
                  <Text>Order details:</Text>

                  <IconButton
                    size="md"
                    fontSize="lg"
                    variant="ghost"
                    color="current"
                    marginLeft="2"
                    onClick={() => setShowConfirmationModal(true)}
                    icon={<FaTrash />}
                    aria-label={`Go to home`}
                  />
                </HStack>

                <Stack>
                  <CheckboxGroup
                    onChange={(values: string[]) => setGroupValues(values)}
                    value={groupValues}
                  >
                    <Checkbox value="paid" my={2}>
                      Paid
                    </Checkbox>
                    <Checkbox value="delivered" my={2}>
                      Delivered
                    </Checkbox>
                    <Checkbox value="physical" my={2}>
                      Physical Delivery Note
                    </Checkbox>
                    <Checkbox value="tra" my={2}>
                      TRA Receipt
                    </Checkbox>
                  </CheckboxGroup>
                </Stack>

                <Stack>
                  {groupValues.includes("physical") && (
                    <Input
                      // color={"white"}
                      fontSize="md"
                      pl={5}
                      //   keyboardType="number-pad"
                      backgroundColor="transparent"
                      variant={"underlined"}
                      inputMode="numeric"
                      defaultValue={deliveryNoteNumber}
                      onChange={(e) => setDeliveryNoteNumber(e.target.value)}
                      placeholder={"Enter delivery note number"}
                    />
                  )}
                </Stack>
                <Stack>
                  {groupValues.includes("tra") && (
                    <>
                      <Input
                        // color={"white"}
                        fontSize="md"
                        pl={5}
                        // keyboardType="number-pad"
                        inputMode="numeric"
                        backgroundColor="transparent"
                        variant={"underlined"}
                        onChange={(e) => setTraReceiptNumber(e.target.value)}
                        placeholder={"Enter TRA Receipt Number"}
                      />

                      <Input
                        fontSize="md"
                        pl={5}
                        backgroundColor="transparent"
                        inputMode="numeric"
                        variant={"underlined"}
                        onChange={(e) => setTraReceiptTime(e.target.value)}
                        placeholder={"Enter Receipt Time"}
                      />
                    </>
                  )}
                </Stack>
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="ghost"
                    colorScheme="blueGray"
                    onClick={() => {
                      setShowDetailsModal(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      updateTransactionDetails();
                    }}
                  >
                    Update
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* CONFIRMATION MODAL */}
          <Modal
            closeOnOverlayClick={false}
            isOpen={showConfirmationModal}
            onClose={() => setShowConfirmationModal(false)}
          >
            <ModalContent maxWidth="350">
              <ModalCloseButton />
              <ModalHeader>Alert</ModalHeader>
              <ModalBody>
                <Stack>
                  <Text textAlign={"center"}>
                    Are you sure you want to VOID this receipt?
                  </Text>
                  <Text textAlign={"center"}>THIS ACTION IS IRREVERSIBLE!</Text>
                </Stack>
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="ghost"
                    colorScheme="blueGray"
                    onClick={() => {
                      setShowConfirmationModal(false);
                    }}
                  >
                    No
                  </Button>
                  <Button
                    bg={"red"}
                    onClick={() => {
                      voidTransaction();
                    }}
                  >
                    Yes
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default PendingOrdersScreen;
